/**
 * RiskScore.tsx — Prominent circular risk gauge component.
 *
 * Renders a colour-coded score (1–100) with a label and optional trend badge.
 * The colour transitions from green (low) → amber (medium) → orange (high)
 * → red (critical) matching the risk engine label boundaries.
 */

import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface RiskScoreProps {
  /** Score value between 1 and 100 */
  score: number | null;
  /** Human-readable risk label */
  label?: string | null;
  /** Trend direction from the predictor */
  trend?: 'rising' | 'stable' | 'falling';
  /** Size variant */
  size?: 'small' | 'large';
}

function scoreToColor(score: number): string {
  if (score <= 25) return '#2dc653';   // green
  if (score <= 50) return '#f4a11d';   // amber
  if (score <= 75) return '#f77f00';   // orange
  return '#ef233c';                     // red
}

function labelToChipColor(label: string): 'success' | 'warning' | 'error' | 'default' {
  switch (label) {
    case 'low': return 'success';
    case 'medium': return 'warning';
    case 'high': return 'warning';
    case 'critical': return 'error';
    default: return 'default';
  }
}

const TREND_ICONS = {
  rising: <TrendingUpIcon fontSize="small" color="error" />,
  stable: <TrendingFlatIcon fontSize="small" color="action" />,
  falling: <TrendingDownIcon fontSize="small" color="success" />,
};

export default function RiskScore({ score, label, trend, size = 'large' }: RiskScoreProps) {
  const dim = size === 'large' ? 120 : 72;
  const fontSize = size === 'large' ? '2rem' : '1.2rem';
  const thickness = size === 'large' ? 4 : 3.5;

  if (score === null || score === undefined) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Box position="relative" display="inline-flex" width={dim} height={dim}>
          <CircularProgress
            variant="determinate"
            value={0}
            size={dim}
            thickness={thickness}
            sx={{ color: 'grey.700' }}
          />
          <Box
            position="absolute" top={0} left={0} bottom={0} right={0}
            display="flex" alignItems="center" justifyContent="center"
          >
            <Typography variant="caption" color="text.secondary">N/A</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const color = scoreToColor(score);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
      <Tooltip title={`Risk Score: ${score}/100 (higher = more risk)`} arrow>
        <Box position="relative" display="inline-flex" width={dim} height={dim}>
          {/* Background track */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={dim}
            thickness={thickness}
            sx={{ color: 'grey.800', position: 'absolute', top: 0, left: 0 }}
          />
          {/* Coloured score arc */}
          <CircularProgress
            variant="determinate"
            value={score}
            size={dim}
            thickness={thickness}
            sx={{ color }}
          />
          {/* Score label in the centre */}
          <Box
            position="absolute" top={0} left={0} bottom={0} right={0}
            display="flex" flexDirection="column" alignItems="center" justifyContent="center"
          >
            <Typography sx={{ fontSize, fontWeight: 700, color, lineHeight: 1 }}>
              {score}
            </Typography>
            {size === 'large' && (
              <Typography variant="caption" color="text.secondary" lineHeight={1}>
                / 100
              </Typography>
            )}
          </Box>
        </Box>
      </Tooltip>

      {label && (
        <Chip
          label={label.toUpperCase()}
          color={labelToChipColor(label)}
          size="small"
          sx={{ fontWeight: 700, letterSpacing: 0.5 }}
        />
      )}

      {size === 'large' && (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', mt: -0.5 }}>
          higher = more risk
        </Typography>
      )}

      {trend && size === 'large' && (
        <Box display="flex" alignItems="center" gap={0.5}>
          {TREND_ICONS[trend]}
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {trend}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
